import { useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { DealsSearchForm, EmptyDealsPlaceholder } from '@crm/entities/deal';
import ResetIcon from '@crm/shared/assets/icons/reload.svg?react';
import EmptyFolderImage from '@crm/shared/assets/images/empty-folder.png';
import LoopWithQuestionMarkImage from '@crm/shared/assets/images/loop.png';
import { useInitialTableRowsLimit } from '@crm/shared/lib';
import { Button, FilterDatePicker, ListPagination, SvgIcon } from '@crm/shared/ui';

import { useGetPostponedDealsQuery } from './api';
import { DEALS_TABLE_LIMIT_OPTIONS, useFilterByQuery } from './model';
import { DealsTable } from './ui/DealsTable';
import { ObjectAreaRange } from './ui/Filters';

export const PostponedDealsPage = () => {
    const { t } = useTranslation();
    const tableViewportRef = useRef<HTMLElement>(null);
    const limit = useInitialTableRowsLimit(tableViewportRef, DEALS_TABLE_LIMIT_OPTIONS);
    const {
        areaRangeProps,
        dateFilterProps,
        hasDataFilters,
        paginationProps,
        queryParams,
        resetFilters,
        searchFormProps,
        tableSortProps,
    } = useFilterByQuery({ limit: limit ?? 1 });
    const { data, isFetching, isSuccess } = useGetPostponedDealsQuery(queryParams, {
        enabled: limit !== null,
    });

    const renderEmptyDealsPlaceholder = () => {
        if (hasDataFilters) {
            return <EmptyDealsPlaceholder img={LoopWithQuestionMarkImage} onReset={resetFilters} />;
        }

        return (
            <EmptyDealsPlaceholder
                img={EmptyFolderImage}
                title={t('crm.postponedPage.empty.title')}
                description={t('crm.postponedPage.empty.description')}
            />
        );
    };

    const renderDealsContent = () => {
        if (!isSuccess) {
            return null;
        }

        if (data?.items.length === 0) {
            return renderEmptyDealsPlaceholder();
        }

        return (
            <DealsTable
                dealItems={data.items}
                pipelineColumns={data.pipelineColumnsData?.columns}
                {...tableSortProps}
            />
        );
    };

    return (
        <div className="flex h-full min-h-0 w-full flex-col gap-4">
            <header className="shrink-0 flex flex-col gap-3.5">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-semibold text-gray-800">
                        {t('crm.postponedPage.title')}
                    </h1>
                    <DealsSearchForm {...searchFormProps} />
                </div>
                <div className="flex gap-2">
                    <FilterDatePicker
                        labelText={t('crm.postponedPage.filters.postponedUntil')}
                        {...dateFilterProps.postponedUntil}
                    />
                    {/* <FilterDatePicker
                        labelText="Последний контакт"
                        {...dateFilterProps.lastContactAt}
                    /> */}
                    <ObjectAreaRange unit={data?.contextData.units.area} {...areaRangeProps} />
                    {hasDataFilters && (
                        <Button variant="outline" onClick={resetFilters}>
                            <SvgIcon icon={ResetIcon} />
                            {t('button.reset')}
                        </Button>
                    )}
                </div>
            </header>
            <main
                ref={tableViewportRef}
                className={`min-h-0 flex-1 overflow-hidden transition-opacity ${
                    isFetching && isSuccess ? 'opacity-30' : ''
                }`}
            >
                {renderDealsContent()}
            </main>
            <footer className="relative shrink-0">
                <ListPagination
                    pagination={data?.pagination}
                    summary={
                        data
                            ? t('crm.common.dealsTable.pagination.summary', {
                                  shown: data.items.length,
                                  total: data.pagination.total,
                              })
                            : undefined
                    }
                    {...paginationProps}
                />
            </footer>
        </div>
    );
};
